/**
 * useLiveEventStatus
 *
 * Single source of truth for the never-cached live event status (date/countdown
 * override + liveness via the backend Gigcasters probe — no client HLS probe).
 *
 * Polling cadence is driven by the lifecycle (see utils/eventLifecycle):
 *   - >1h before start            → no network; 60s local timer watches threshold
 *   - within 1h / starting-soon   → poll every 30s
 *   - live                        → slow re-poll every 60s (catch a mid-stream drop)
 *   - replay (vod) / ended / past → stop (replay is stable)
 * The window is decided from the cached slim data (start_time ?? event_date, and
 * end_time ?? start+4h), so far-future and long-past events make no request.
 * `refresh()` is also exposed for the countdown-zero moment.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import cmsService from '../services/CMSService';
import {
  computeEndBoundary,
  computePollMode,
  POLL_ACTIVE_MS,
  POLL_LIVE_MS,
  GATE_CHECK_MS,
} from '../utils/eventLifecycle';

function parseMs(raw) {
  if (!raw) return null;
  const m = moment.utc(raw);
  return m.isValid() ? m.valueOf() : null;
}

// Event window from the cached slim data — used only to gate the backend call.
function slimWindow(gigcasterId) {
  if (!gigcasterId) return { startMs: null, endMs: null };
  const slim = cmsService.getSlimEventData(gigcasterId);
  if (!slim) return { startMs: null, endMs: null };
  const startMs = parseMs(slim.start_time ?? slim.event_date);
  return { startMs, endMs: computeEndBoundary(startMs, parseMs(slim.end_time)) };
}

export function useLiveEventStatus(gigcasterId) {
  const [status, setStatus] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!gigcasterId) {
      setStatus(null);
      return;
    }
    try {
      const data = await cmsService.getLiveStatus(gigcasterId);
      if (isMountedRef.current) setStatus(data);
    } catch (error) {
      // Keep the previous value on a transient failure — cached CMS data remains
      // the fallback in the consuming logic.
      console.error('[useLiveEventStatus] fetch failed:', error);
    }
  }, [gigcasterId]);

  const { startMs, endMs } = slimWindow(gigcasterId);
  const streamState = status?.stream_state ?? null;

  // Cadence driven by lifecycle pollMode. Re-runs when streamState changes
  // (e.g. down→live→vod) so the interval switches or stops automatically.
  useEffect(() => {
    if (!gigcasterId) return undefined;
    const mode = computePollMode({ effectiveStartMs: startMs, endBoundaryMs: endMs, streamState });
    if (mode === 'stop') return undefined;

    let pollId = null;
    let gateId = null;
    const startPolling = intervalMs => {
      refresh();
      pollId = setInterval(refresh, intervalMs);
    };

    if (mode === 'active') startPolling(POLL_ACTIVE_MS);
    else if (mode === 'slow') startPolling(POLL_LIVE_MS);
    else if (mode === 'gate') {
      gateId = setInterval(() => {
        const m = computePollMode({ effectiveStartMs: startMs, endBoundaryMs: endMs, streamState });
        if (m !== 'gate') {
          clearInterval(gateId);
          gateId = null;
          startPolling(POLL_ACTIVE_MS);
        }
      }, GATE_CHECK_MS);
    }

    return () => {
      if (pollId) clearInterval(pollId);
      if (gateId) clearInterval(gateId);
    };
  }, [gigcasterId, startMs, endMs, streamState, refresh]);

  // Re-fetch on focus / tab visibility — only when we'd actively poll.
  useEffect(() => {
    const onActive = () => {
      if (document.visibilityState !== 'visible') return;
      const mode = computePollMode({
        effectiveStartMs: startMs,
        endBoundaryMs: endMs,
        streamState,
      });
      if (mode === 'active' || mode === 'slow') refresh();
    };
    window.addEventListener('focus', onActive);
    document.addEventListener('visibilitychange', onActive);
    return () => {
      window.removeEventListener('focus', onActive);
      document.removeEventListener('visibilitychange', onActive);
    };
  }, [startMs, endMs, streamState, refresh]);

  const effectiveDate = status ? (status.start_time ?? status.event_date ?? null) : null;

  return {
    hideCountdown: status?.hide_countdown ?? null,
    startTime: status?.start_time ?? null,
    endTime: status?.end_time ?? null,
    eventDate: status?.event_date ?? null,
    /** start_time wins over event_date; null when neither is loaded yet */
    effectiveDate,
    /** Backend Gigcasters probe: encoder actually broadcasting */
    isLive: status?.is_live ?? false,
    /** live OR vod replay — show WATCH NOW */
    streamAvailable: status?.stream_available ?? false,
    streamState,
    loaded: status !== null,
    refresh,
  };
}

export default useLiveEventStatus;
